import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Statistic, Progress, Typography, Space, Row, Col, Table, notification } from "antd";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import metaData from "../../../data/metaData";

//
import Status from "../../../components/Table/RequestStatus";
import ActionHeader from "../../../components/Table/ActionHeader";
import DelaySearch from "../../../components/Table/DelaySearch";
import FilterDropdown from "../../../components/Table/FIlterDropdown";

//
import useAuthStore from "../../../states/useAuth";
import useShopStore from "../../../states/useShop";
import useShop from "../../../hooks/useShop";
import Meta from "../../../components/Meta";
import useRequest from "../../../hooks/useRequest";
import { formatTime } from "../../../utils/time";
import ShopLogo from "../../../components/Shop/Logo";
import ProductTag from "../../../components/Table/ProductTag";

const { Title, Text, Paragraph } = Typography;

const ProductRequest = () => {
  const history = useHistory();
  const location = useLocation();

  const { auth } = useAuthStore();
  const { getProductRequests, resetRequest } = useRequest();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Segment
  const [activeSegment, setAtiveSegment] = useState("new");

  //Filter
  const [searchValue, setSearchValue] = useState();

  //Table
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [data, setData] = useState([]);
  const [filteredDataCount, setFilteredDataCount] = useState(data.length);
  const [isFetchingRequests, setIsFetchingRequests] = useState(false);

  const filterData = (searchValue, data) => {
    if (!searchValue) return data;

    const lowerSearchValue = searchValue.toLowerCase();

    return data.filter((record) => {
      const id = record?.id?.toLowerCase() || "";
      const companyName = record?.company?.name?.toLowerCase() || "";
      const brandName = record?.brand?.name?.toLowerCase() || "";
      const shopName = record?.shop?.name?.toLowerCase() || "";
      const productName = record?.info?.product?.name?.toLowerCase() || "";

      return (
        id.includes(lowerSearchValue) ||
        companyName.includes(lowerSearchValue) ||
        brandName.includes(lowerSearchValue) ||
        shopName.includes(lowerSearchValue) ||
        productName.includes(lowerSearchValue)
      );
    });
  };

  const filteredData = useMemo(() => {
    // Filter your data based on the searchValue
    let filtered = filterData(searchValue, data); // Assuming filterData is defined and works correctly

    setFilteredDataCount(filtered.length);

    // Sort function to prioritize 'pending' over 'approved'
    return filtered.sort((a, b) => {
      // Sort by status first (pending over approved), then by lastModify in descending order
      if (a.status !== b.status) {
        return a.status === "pending" ? -1 : 1;
      }

      return new Date(b.time.lastModify) - new Date(a.time.lastModify);
    });
  }, [data, searchValue]);

  const pageRef = useRef();

  const columns = [
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (text, record) => {
        const isNew = record?.status == "pending" && record?.type == "new";

        return (
          <>
            <Row>{record?.id}</Row>
            <Row>{isNew ? <span style={{ color: "var(--ion-color-red-600)" }}>new!</span> : null}</Row>
          </>
        );
      },
      // render: (text, record) => <Paragraph copyable={{ text: record?.id }}>{record?.id}</Paragraph>,
    },
    {
      title: "Brand / Company",
      dataIndex: "brand.name",
      key: "brand.name",
      render: (text, record) => (
        <Meta
          image={
            <ShopLogo
              style={{ width: 40, height: 40, objectFit: "cover", marginRight: "0.5rem" }}
              logo={record?.logo}
              brandLogo={record?.brand?.logo}
            />
          }
          title={record?.brand?.name}
          description={record?.company?.name}
        />
      ),
      sorter: (a, b) => (a?.brand?.name).localeCompare(b?.brand?.name),
    },
    {
      title: "Product",
      dataIndex: "product.name",
      key: "product.name",
      width: 250,
      render: (text, record) => {
        let { product } = c.getProductFromRequest(record);

        return (
          <Meta
            image={
              <img
                style={{ width: 40, height: 40, objectFit: "cover", marginRight: "0.5rem" }}
                src={metaData.blobPreURL + "clientUpload-" + product?.gallery?.[0] + ".jpeg" + metaData.blobPostURL}
              />
            }
            title={
              <Text
                style={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {product?.name}
              </Text>
            }
            description={<ProductTag product={product} />}
            maxWidth={150}
          />
        );
      },
    },
    {
      title: "Last Modify",
      dataIndex: "lastModifyTime",
      key: "lastModifyTime",
      render: (text, record) => <>{formatTime(record?.time.lastModify)}</>,
      sorter: (a, b) => moment(a.time.lastModify).unix() - moment(b.time.lastModify).unix(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        return (
          <Space>
            <Status status={text} />
          </Space>
        );
      },
      filters: [
        {
          text: "Pending",
          value: "pending",
        },
        {
          text: "Approved",
          value: "approved",
        },
        {
          text: "Need Revision",
          value: "needRevision",
        },
      ],
      onFilter: (value, record) => record?.status.indexOf(value) === 0,
      sorter: (a, b) => (a?.status).localeCompare(b?.status),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (text, record) => (
        <Space
          style={{
            fontSize: "1rem",
          }}
        >
          <a
            onClick={() => {
              history.push({ pathname: "/approver/product/request/" + record.id });
            }}
          >
            detail
          </a>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  };

  const handleActionClick = (key) => {
    log.debug(key);
  };

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();

    setFilteredDataCount(extra.currentDataSource.length);
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getProductRequestsData = async () => {
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    const queryParams = c.parseQueryToJSON(queryURL);
    const { pagination } = queryParams;
    log.debug(pagination);

    let result;
    try {
      setIsFetchingRequests(true);
      result = await getProductRequests(queryURL, false);

      //add key to disable antd table warning
      setData(
        result.map((e, i) => {
          return { ...e, key: i };
        })
      );
    } catch (error) {
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingRequests(false);
      log.debug(result);
    }
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetRequest(); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getProductRequestsData();
  }, [location.search]);

  return (
    <div style={{ margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>Product Request</Title>
        {/* <div>
          <Button size="large" type="primary" icon={<PlusOutlined />} style={{ marginRight: "8px" }}>
            Add product
          </Button>
        </div> */}
      </div>

      <Row justify="space-between" style={{ width: "100%", marginBottom: "2rem" }}>
        <DelaySearch
          style={{ width: "25rem" }}
          placeholder="Search"
          onSearch={(value) => {
            value = value || undefined;
            log.debug(value);
            setSearchValue(value);
          }}
          variant="filled"
          isDelay={false}
        />
      </Row>

      {/* Filter Group */}
      {/* {activeSegment === "all" ? (
                <Card bordered style={{ marginBottom: "20px", background: "var(--ion-color-step-50)" }}>
                  <Row>
                    <FilterDropdown
                      label="Status"
                      defaultValue="all"
                      options={[
                        { value: "all", label: "All Status" },
                        { value: "pending", label: "Pending" },
                        { value: "approved", label: "Approved" },
                        { value: "rejected", label: "Rejected" },
                      ]}
                    />
                  </Row>
                </Card>
              ) : null} */}

      <ActionHeader
        pagination={pagination}
        pageSize={20}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        count={filteredDataCount}
        onActionClick={handleActionClick}
      />

      <Table
        className="white-table-header"
        // rowSelection={rowSelection} disabled multiple selection
        rowSelection={undefined}
        sticky
        columns={columns}
        loading={isFetchingRequests}
        dataSource={filteredData}
        pagination={{
          current: pagination,
          pageSize: 20,
          position: ["none", "bottomCenter"],
          showSizeChanger: false,
        }}
        onChange={handleTableChange} // This handles the next/prev page
      />

      <Row style={{ marginBottom: "5rem" }}></Row>
    </div>
  );
};

export default ProductRequest;
